import type React from 'react';
import { useAccess } from '@/lib/sharing/hooks/use-access';
import { canView, canEdit, canManage, canDelete, canRemoveSelf } from '@/lib/sharing/utils/permissions';
import type { Access } from '@/lib/sharing/utils/permissions';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Requirement = 'view' | 'edit' | 'manage' | 'delete' | 'removeSelf';

const CHECKS: Record<Requirement, (a: Access) => boolean> = {
  view: canView,
  edit: canEdit,
  manage: canManage,
  delete: canDelete,
  removeSelf: canRemoveSelf,
};

interface PermissionGateProps {
  resourceId: string;
  require: Requirement;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

// ---------------------------------------------------------------------------
// PermissionGate
//
// JSX-level gating primitive. Renders children only when the current user
// has (at least) the required permission on the given resource.
//
// When the resourceId is not in the entity store (loading or no access),
// the gate blocks children and shows the fallback.
// ---------------------------------------------------------------------------

export function PermissionGate({
  resourceId,
  require,
  fallback = null,
  children,
}: PermissionGateProps) {
  const access = useAccess(resourceId);
  if (!access) return <>{fallback}</>;
  return CHECKS[require](access) ? <>{children}</> : <>{fallback}</>;
}

// ---------------------------------------------------------------------------
// usePermission
//
// Hook equivalent of PermissionGate. Returns { loading, readOnly } so callers
// can conditionally render editable vs read-only variants of any entity kind.
//
// `loading` is true while access hasn't resolved yet — callers should block
// rendering until it settles to prevent a read-only user briefly seeing the
// editable surface.
// ---------------------------------------------------------------------------

export function usePermission(resourceId: string): { loading: boolean; readOnly: boolean } {
  const access = useAccess(resourceId);
  if (access === null) return { loading: true, readOnly: true };
  return { loading: false, readOnly: !canEdit(access) };
}
